import React from 'react';
import { Input } from '@/components/ui/Input';
import { formatDateForInput } from '@/utils/format';
import type { MetaBudgetMode } from './MetaBudgetStep';

interface MetaScheduleStepProps {
  budgetMode: MetaBudgetMode;
  startDate: string;       // YYYY-MM-DD (HTML date-input shape)
  stopDate: string;        // YYYY-MM-DD; required when budgetMode === 'lifetime'
  /** Daily-budget only — controls whether stopDate is rendered. Lifetime
   *  budget always shows stopDate (Meta requires it). */
  hasEndDate: boolean;
  /** Today's YYYY-MM-DD for the date input's `min` attribute (no past dates). */
  today: string;
  onChangeStartDate: (date: string) => void;
  onChangeStopDate: (date: string) => void;
  onChangeHasEndDate: (hasEndDate: boolean) => void;
  errors?: {
    startDate?: string;
    stopDate?: string;
  };
}

/**
 * Step 4 — schedule.
 *
 * Start date is REQUIRED (D7 strictness — parity with Google's wizard) and
 * cannot be in the past. Stop date is conditional:
 *  - Daily budget: shown only when `hasEndDate` is true (toggle).
 *  - Lifetime budget: always shown and required (Meta enforces this).
 *
 * The `today` prop gates the `<input type="date">` `min` attribute so the
 * browser-native date picker rejects past dates before the user even
 * confirms.
 */
export const MetaScheduleStep: React.FC<MetaScheduleStepProps> = ({
  budgetMode,
  startDate,
  stopDate,
  hasEndDate,
  today,
  onChangeStartDate,
  onChangeStopDate,
  onChangeHasEndDate,
  errors,
}) => {
  const isLifetime = budgetMode === 'lifetime';
  const showStopDate = isLifetime || hasEndDate;

  // HTML5 `<input type="date">` `min` is inclusive, but the wizard's JS
  // validator rejects `stopDate <= startDate` (Meta requires non-zero
  // duration). Floor the stop-date picker at start+1 so the picker doesn't
  // offer the user a date the validator would then reject. Uses local
  // date arithmetic (matches the `formatDateForInput` convention applied
  // elsewhere — `toISOString().slice(0,10)` would slip a day for
  // west-of-UTC users in the evening).
  const stopDateMin = startDate
    ? (() => {
        const [y, m, d] = startDate.split('-').map(Number);
        const next = new Date(y, m - 1, d + 1);
        return formatDateForInput(next);
      })()
    : today;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Schedule</h3>
        <p className="text-sm text-gray-600">
          {isLifetime
            ? 'Lifetime budgets need both a start and stop date.'
            : 'Start date is required. Add an optional stop date if you want the campaign to end automatically.'}
        </p>
      </div>

      <div>
        <label
          htmlFor="meta-start-date"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Start date <span className="text-red-500" aria-label="required">*</span>
        </label>
        <Input
          id="meta-start-date"
          type="date"
          value={startDate}
          min={today}
          onChange={(e) => onChangeStartDate(e.target.value)}
          aria-invalid={Boolean(errors?.startDate)}
          aria-describedby={errors?.startDate ? 'meta-start-date-error' : undefined}
          required
        />
        {errors?.startDate && (
          <p
            id="meta-start-date-error"
            role="alert"
            className="text-sm text-red-600 mt-1"
          >
            {errors.startDate}
          </p>
        )}
      </div>

      {/* End-date toggle — daily-budget mode only. Lifetime always shows
          stopDate because Meta requires it. */}
      {!isLifetime && (
        <div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={hasEndDate}
              onChange={(e) => onChangeHasEndDate(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">
              Set an end date
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Leave off to run the campaign indefinitely (you can pause or
            archive it later).
          </p>
        </div>
      )}

      {showStopDate && (
        <div>
          <label
            htmlFor="meta-stop-date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Stop date
            {isLifetime ? (
              <span className="text-red-500 ml-1" aria-label="required">*</span>
            ) : null}
          </label>
          <Input
            id="meta-stop-date"
            type="date"
            value={stopDate}
            onChange={(e) => onChangeStopDate(e.target.value)}
            min={stopDateMin}
            aria-invalid={Boolean(errors?.stopDate)}
            aria-describedby={errors?.stopDate ? 'meta-stop-date-error' : undefined}
            required={isLifetime}
          />
          {errors?.stopDate && (
            <p
              id="meta-stop-date-error"
              role="alert"
              className="text-sm text-red-600 mt-1"
            >
              {errors.stopDate}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
